import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, ToggleButton, ToggleButtonGroup, CircularProgress,
  FormControlLabel, Switch
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Save, Settings, Code, ChevronRight } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { locale, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt  = (n) => n === null || n === undefined ? '∞' : Number(n).toLocaleString();

// Only rates used by formula token rate('CODE'). PAYE reliefs moved to PayrollSetupConfig.
const RATE_LABELS = {
  SHIF_RATE:       'SHIF Rate',
  HOUSING_LEVY_EE: 'Housing Levy — Employee',
  HOUSING_LEVY_ER: 'Housing Levy — Employer',
};
const RATE_UNITS = {
  SHIF_RATE: '%', HOUSING_LEVY_EE: '%', HOUSING_LEVY_ER: '%',
};

const emptyBand = { bandNo: '', lowerLimit: '', upperLimit: '', rate: '', effectiveDate: '' };

const PayrollSetupPage = () => {
  const navigate = useNavigate();

  const [bands,    setBands]    = useState([]);
  const [rates,    setRates]    = useState([]);
  const [, setConfig] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  // PAYE band dialog
  const [bandDlg,  setBandDlg]  = useState(false);
  const [bandForm, setBandForm] = useState(emptyBand);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: bandDlg, value: bandForm, onClose: () => setBandDlg(false) });
  const [editId,   setEditId]   = useState(null);
  const [saving,   setSaving]   = useState(false);

  // Inline rate editing
  const [editRate, setEditRate] = useState(null);
  const [rateVal,  setRateVal]  = useState('');

  // Payroll config editing
  const [cfgForm,   setCfgForm]   = useState(null);
  const [cfgSaving, setCfgSaving] = useState(false);

  // Delete band confirmation
  const [deleteBandDlg, setDeleteBandDlg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, rRes, cRes] = await Promise.all([
        axios.get(`${API}/api/payroll/paye-bands`,          { headers: hdrs() }),
        axios.get(`${API}/api/payroll/statutory-rates`,     { headers: hdrs() }),
        axios.get(`${API}/api/payroll/payroll-setup-config`,{ headers: hdrs() }),
      ]);
      setBands(bRes.data);
      setRates(rRes.data);
      setConfig(cRes.data);
      setCfgForm(cRes.data);
    } catch { setError('Failed to load payroll setup'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── PAYE Band CRUD ──

  const openBandDlg = (band = null) => {
    if (band) {
      setBandForm({
        bandNo:        band.bandNo,
        lowerLimit:    band.lowerLimit,
        upperLimit:    band.upperLimit ?? '',
        rate:          band.rate,
        effectiveDate: band.effectiveDate ? band.effectiveDate.split('T')[0] : '',
      });
      setEditId(band._id);
    } else {
      const nextNo = bands.length ? Math.max(...bands.map(b => b.bandNo)) + 1 : 1;
      setBandForm({ ...emptyBand, bandNo: nextNo });
      setEditId(null);
    }
    setBandDlg(true);
  };

  const saveBand = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/payroll/paye-bands`, {
        ...bandForm,
        upperLimit: bandForm.upperLimit === '' ? null : Number(bandForm.upperLimit),
      }, { headers: hdrs() });
      setSuccess('Band saved'); setBandDlg(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const confirmDeleteBand = async () => {
    if (!deleteBandDlg) return;
    try {
      await axios.delete(`${API}/api/payroll/paye-bands/${deleteBandDlg._id}`, { headers: hdrs() });
      setSuccess('Deleted'); setDeleteBandDlg(null); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  // ── Statutory Rates ──

  const startEditRate = (rate) => {
    setEditRate(rate);
    setRateVal(String(rate.value));
  };

  const saveRate = async () => {
    if (!editRate) return;
    try {
      await axios.put(`${API}/api/payroll/statutory-rates/${editRate.code}`,
        { value: parseFloat(rateVal), description: editRate.description },
        { headers: hdrs() }
      );
      setEditRate(null); setSuccess('Rate saved'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
  };

  // ── Payroll Config ──

  const saveCfg = async () => {
    setCfgSaving(true); setError('');
    try {
      await axios.put(`${API}/api/payroll/payroll-setup-config`, cfgForm, { headers: hdrs() });
      setSuccess('Configuration saved'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setCfgSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 1050, mx: 'auto', mt: 4, mb: 6 }}>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/payroll')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Settings />
              <Typography variant="h6" fontWeight={700}>Payroll Setup</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!loading && (
        <>
          {/* ── Section 0: Transaction Codes quick-link ── */}
          <Card elevation={2} sx={{ mb: 3, cursor: 'pointer', borderLeft: '4px solid #e65100', '&:hover': { boxShadow: 4 } }}
            onClick={() => navigate('/payroll/setup/transactions')}>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Code sx={{ color: '#e65100' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Transaction Codes</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add, edit or disable earnings and deductions — each with its own formula, sequence and behaviour
                    </Typography>
                  </Box>
                </Box>
                <ChevronRight sx={{ color: 'text.secondary' }} />
              </Box>
            </CardContent>
          </Card>

          {/* ── Section 1: PAYE Tax Bands ── */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>PAYE Tax Bands</Typography>
                  <Typography variant="caption" color="text.secondary">Kenya progressive income tax brackets</Typography>
                </Box>
                <Button startIcon={<Add />} variant="contained" size="small"
                  sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}
                  onClick={() => openBandDlg()}>
                  Add Band
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Band</strong></TableCell>
                    <TableCell align="right"><strong>From ({sym()})</strong></TableCell>
                    <TableCell align="right"><strong>To ({sym()})</strong></TableCell>
                    <TableCell align="right"><strong>Rate (%)</strong></TableCell>
                    <TableCell><strong>Effective From</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bands.sort((a, b) => a.bandNo - b.bandNo).map(b => (
                    <TableRow key={b._id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>Band {b.bandNo}</Typography></TableCell>
                      <TableCell align="right">{fmt(b.lowerLimit)}</TableCell>
                      <TableCell align="right">{fmt(b.upperLimit)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="primary.main">{b.rate}%</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {b.effectiveDate ? new Date(b.effectiveDate).toLocaleDateString(locale()) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openBandDlg(b)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteBandDlg(b)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                Personal Relief is configured in Statutory Rates below. Insurance relief = 15% of premiums, capped at the Insurance Relief Max.
              </Typography>
            </CardContent>
          </Card>

          {/* ── Section 2: Statutory Rates ── */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Statutory Rates</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Rates referenced by formula token <code>rate('CODE')</code>. PAYE reliefs and pension caps are in Payroll Configuration below.
              </Typography>
              <Grid container spacing={2}>
                {rates.map(r => (
                  <Grid item xs={12} sm={6} md={4} key={r.code}>
                    {editRate?.code === r.code ? (
                      <Box sx={{ p: 1.5, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
                        <Typography variant="caption" fontWeight={700} color="primary">{RATE_LABELS[r.code] || r.description}</Typography>
                        <Box display="flex" gap={1} mt={1}>
                          <TextField
                            size="small" type="number" value={rateVal} autoFocus
                            onChange={e => setRateVal(e.target.value)}
                            InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>{RATE_UNITS[r.code] || ''}</Typography> }}
                          />
                          <Button size="small" variant="contained" onClick={saveRate}><Save fontSize="small" /></Button>
                          <Button size="small" onClick={() => setEditRate(null)}>✕</Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        onClick={() => startEditRate(r)}
                        sx={{
                          p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, cursor: 'pointer',
                          '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f5f5' }
                        }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {RATE_LABELS[r.code] || r.description}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="primary.main" lineHeight={1.2} mt={0.3}>
                          {r.value}{RATE_UNITS[r.code]?.startsWith('%') ? '%' : ''}
                        </Typography>
                        {!RATE_UNITS[r.code]?.startsWith('%') && (
                          <Typography variant="caption" color="text.secondary">{sym()}/month</Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* ── Section 3: Payroll Configuration ── */}
          {cfgForm && (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Payroll Configuration</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Global policy settings — applies to all payroll calculations from the next run.
                </Typography>

                {/* NSSF */}
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8} display="block" mb={1}>
                  NSSF (National Social Security Fund)
                </Typography>
                <Grid container spacing={2} alignItems="flex-start" mb={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" fontWeight={600} display="block" mb={1}>Contribution Mode</Typography>
                    <ToggleButtonGroup
                      exclusive value={cfgForm.nssfMode}
                      onChange={(_, val) => val && setCfgForm(f => ({ ...f, nssfMode: val }))}
                      size="small">
                      <ToggleButton value="TIERED">TIERED (Act 2013)</ToggleButton>
                      <ToggleButton value="FLAT_RATE">Flat Rate</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  {cfgForm.nssfMode === 'TIERED' ? (
                    <>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" fontWeight={600} display="block" mb={1}>Tier I</Typography>
                        <Box display="flex" gap={1}>
                          <TextField label={`LEL (${sym()})`} size="small" type="number" value={cfgForm.tierICeiling}
                            onChange={e => setCfgForm(f => ({ ...f, tierICeiling: Number(e.target.value) }))} sx={{ width: 120 }} />
                          <TextField label="Rate %" size="small" type="number" value={cfgForm.tierIRate * 100}
                            onChange={e => setCfgForm(f => ({ ...f, tierIRate: Number(e.target.value) / 100 }))}
                            InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }} sx={{ width: 90 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" fontWeight={600} display="block" mb={1}>Tier II</Typography>
                        <Box display="flex" gap={1}>
                          <TextField label={`UEL (${sym()})`} size="small" type="number" value={cfgForm.tierIICeiling}
                            onChange={e => setCfgForm(f => ({ ...f, tierIICeiling: Number(e.target.value) }))} sx={{ width: 120 }} />
                          <TextField label="Rate %" size="small" type="number" value={cfgForm.tierIIRate * 100}
                            onChange={e => setCfgForm(f => ({ ...f, tierIIRate: Number(e.target.value) / 100 }))}
                            InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }} sx={{ width: 90 }} />
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={3}>
                        <TextField label={`Employee (${sym()}/month)`} size="small" type="number" fullWidth
                          value={cfgForm.flatEmployee}
                          onChange={e => setCfgForm(f => ({ ...f, flatEmployee: Number(e.target.value) }))} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField label={`Employer (${sym()}/month)`} size="small" type="number" fullWidth
                          value={cfgForm.flatEmployer}
                          onChange={e => setCfgForm(f => ({ ...f, flatEmployer: Number(e.target.value) }))} />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* PAYE Reliefs */}
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8} display="block" mb={1}>
                  PAYE Reliefs
                </Typography>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField label={`Personal Relief (${sym()}/month)`} size="small" type="number" fullWidth
                      value={cfgForm.personalRelief}
                      onChange={e => setCfgForm(f => ({ ...f, personalRelief: Number(e.target.value) }))}
                      helperText="Deducted from raw PAYE (KRA standard = 2,400)" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Insurance Relief (%)" size="small" type="number" fullWidth
                      value={cfgForm.insuranceReliefPct}
                      onChange={e => setCfgForm(f => ({ ...f, insuranceReliefPct: Number(e.target.value) }))}
                      helperText="% of insurance premiums (KRA standard = 15%)" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label={`Insurance Relief Cap (${sym()}/month)`} size="small" type="number" fullWidth
                      value={cfgForm.insuranceReliefMax}
                      onChange={e => setCfgForm(f => ({ ...f, insuranceReliefMax: Number(e.target.value) }))}
                      helperText="Maximum insurance relief per month (= 5,000)" />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Pension / PRMF Caps */}
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8} display="block" mb={1}>
                  Deduction Caps
                </Typography>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField label={`Max Pension Relief (${sym()}/month)`} size="small" type="number" fullWidth
                      value={cfgForm.maxPension}
                      onChange={e => setCfgForm(f => ({ ...f, maxPension: Number(e.target.value) }))}
                      helperText="Registered pension: KRA allowable cap = 20,000" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label={`Max PRMF (${sym()}/month)`} size="small" type="number" fullWidth
                      value={cfgForm.maxPRMF}
                      onChange={e => setCfgForm(f => ({ ...f, maxPRMF: Number(e.target.value) }))}
                      helperText="Post-retirement medical fund cap = 20,000" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label={`PWD Tax Limit (${sym()}/month)`} size="small" type="number" fullWidth
                      value={cfgForm.pwdTaxLimit}
                      onChange={e => setCfgForm(f => ({ ...f, pwdTaxLimit: Number(e.target.value) }))}
                      helperText="Persons with Disability exemption limit = 150,000" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Excess Pension Tax Rate (%)" size="small" type="number" fullWidth
                      value={cfgForm.excessPensionTaxRate ?? 30}
                      onChange={e => setCfgForm(f => ({ ...f, excessPensionTaxRate: Number(e.target.value) }))}
                      helperText="Tax on pension above the KRA cap (normally 30%)" />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Policy Switches */}
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8} display="block" mb={1}>
                  Policy Switches
                </Typography>
                <Grid container spacing={1} mb={2}>
                  {[
                    { field: 'checkAThirdRule',   label: 'Enforce A-Third Rule',    help: 'Net pay must not fall below 1/3 of basic salary (Kenya Employment Act s.19). Flags violations on payroll entries.' },
                    { field: 'prorateAllowances', label: 'Prorate Allowances',       help: 'Scale allowances for part-period employees (joiners / leavers)' },
                    { field: 'splitNssfTiers',    label: 'Split NSSF Tiers on Payslip', help: 'Show Tier I and Tier II separately on payslips instead of combined' },
                  ].map(({ field, label, help }) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2,
                        bgcolor: cfgForm[field] ? '#e8f5e9' : 'inherit',
                        borderColor: cfgForm[field] ? '#a5d6a7' : '#e0e0e0' }}>
                        <FormControlLabel
                          control={
                            <Switch size="small" checked={!!cfgForm[field]}
                              onChange={e => setCfgForm(f => ({ ...f, [field]: e.target.checked }))} />
                          }
                          label={<Box>
                            <Typography variant="body2" fontWeight={600}>{label}</Typography>
                            <Typography variant="caption" color="text.secondary">{help}</Typography>
                          </Box>}
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button variant="contained" startIcon={<Save />} onClick={saveCfg} disabled={cfgSaving}
                  sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
                  {cfgSaving ? 'Saving…' : 'Save Configuration'}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Delete Band Confirmation Dialog */}
      <Dialog open={Boolean(deleteBandDlg)} onClose={() => setDeleteBandDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete PAYE Band</DialogTitle>
        <DialogContent>
          <Typography>
            Delete Band {deleteBandDlg?.bandNo} ({deleteBandDlg?.lowerLimit} — {deleteBandDlg?.upperLimit ?? '∞'} @ {deleteBandDlg?.rate}%)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBandDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteBand}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* PAYE Band Dialog */}
      {discardDialog}
      <Dialog open={bandDlg} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editId ? 'Edit' : 'Add'} PAYE Band</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={6}>
              <TextField label="Band No" type="number" size="small" fullWidth value={bandForm.bandNo}
                onChange={e => setBandForm(f => ({ ...f, bandNo: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Rate (%)" type="number" size="small" fullWidth value={bandForm.rate}
                onChange={e => setBandForm(f => ({ ...f, rate: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label={`From (${sym()})`} type="number" size="small" fullWidth value={bandForm.lowerLimit}
                onChange={e => setBandForm(f => ({ ...f, lowerLimit: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label={`To (${sym()}) — blank = ∞`} type="number" size="small" fullWidth value={bandForm.upperLimit}
                onChange={e => setBandForm(f => ({ ...f, upperLimit: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Effective Date" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }} value={bandForm.effectiveDate}
                onChange={e => setBandForm(f => ({ ...f, effectiveDate: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveBand} disabled={saving}
            sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollSetupPage;
